import { useQuery } from '@tanstack/react-query';
import { useApolloClient } from '@apollo/client/react';
import { GET_CONTACTS } from '../../lib/graphql/queries/contacts';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import type { GQLContactConsent } from '../graphql/contacts/useContact';

export const PAGE_SIZE = 25;

export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  tags: string[];
  optIn: boolean;
  call_tester?: boolean;
  opt_in?: boolean;
  consent?: GQLContactConsent | null;
  [key: string]: unknown;
}

export interface QueryResult {
  contacts: Contact[];
  cursor: number | null;
  totalCount: number;
}

interface GQLContactsData {
  contacts: {
    contacts: Array<{
      contactId: string;
      firstName: string;
      lastName: string;
      phone: string;
      tags: string[];
      optIn: boolean;
      callTester: boolean | null;
      consent: GQLContactConsent | null;
    }>;
    total: number;
    hasMore: boolean;
  };
}

function mapGQLToContact(c: GQLContactsData['contacts']['contacts'][0]): Contact {
  return {
    id: c.contactId,
    firstName: c.firstName ?? '',
    lastName: c.lastName ?? '',
    phoneNumber: c.phone ?? '',
    tags: c.tags ?? [],
    optIn: c.optIn ?? false,
    opt_in: c.optIn ?? false,
    call_tester: c.callTester ?? false,
    consent: c.consent,
  };
}

export const useContactsByTags = (
  tags: string[],
  _flowId?: string,
  shouldFetch: boolean = false,
  page: number = 0,
  pageSize: number = PAGE_SIZE
) => {
  const { organization } = useOrganizationStore();
  const apolloClient = useApolloClient();

  const { data, isLoading, isFetching } = useQuery<QueryResult, Error>({
    queryKey: ['contacts', 'byTags', organization?.id, tags, page, pageSize],
    queryFn: async () => {
      if (!organization?.id || !tags.length) return { contacts: [], cursor: null, totalCount: 0 };
      const offset = page * pageSize;

      const { data } = await apolloClient.query<GQLContactsData>({
        query: GET_CONTACTS,
        variables: {
          organizationId: organization.id,
          tags,
          matchAllTags: true,
          limit: pageSize,
          offset,
          sortOrder: 'ASC',
        },
        fetchPolicy: 'network-only',
      });

      const contacts = (data?.contacts?.contacts ?? []).map(mapGQLToContact);

      return {
        contacts,
        cursor: null,
        totalCount: data?.contacts?.total ?? 0,
      };
    },
    enabled: !!organization?.id && tags.length > 0 && shouldFetch,
  });

  // A disabled query still returns what it cached for these tags, so a tag
  // change back to an earlier search would show that list before Search runs.
  const searched = shouldFetch ? data : undefined;

  return {
    contacts: searched?.contacts ?? [],
    totalCount: searched?.totalCount ?? 0,
    isLoading,
    isFetching,
  };
};
